/**
 * LGBTQ+ relationship support content — bilingual EN/ES.
 *
 * Six trauma-informed articles. Tone is gentle, non-clinical, non-political.
 * Article slugs are stable so they can be linked from elsewhere in the app.
 *
 * Personalisation surface (computed at read time, not at storage time):
 *   - partner_gender === "woman"     → "both-women" article foregrounded
 *   - partner_gender === "nonbinary" → "no-gender" + "green-flags" foregrounded
 *   - everyone else                  → default order below
 */

const en = [
  {
    slug: "no-gender",
    title: "Abuse doesn't have a gender",
    lede: "Abuse is not defined by gender. It is a pattern of control, fear, manipulation, coercion, intimidation, isolation, or harm.",
    sections: [
      { h: "What this means",
        body: "Many survivors in same-sex and queer relationships struggle to recognise abuse because society quietly assumes abuse only happens between men and women. The pattern is the same. The harm is the same. The recognition just takes longer." },
      { h: "What the pattern looks like, regardless of gender", list: [
        "A relationship where you feel afraid, smaller, or constantly wrong.",
        "Affection that comes and goes in ways that don't match what you did.",
        "Apologies that arrive only when you're about to leave.",
        "A pull to defend her or him to your friends, even when you're hurting.",
      ]},
      { h: "What is true",
        body: "If your nervous system is bracing every time the door opens, that is information. If you are minimising it because \"women don't hurt women\" or \"men don't hurt men\" — that script is from outside your relationship. It does not have to be your script." },
    ],
  },
  {
    slug: "coercive-control",
    title: "Signs of coercive control",
    lede: "Coercive control is a pattern of behaviour that, in aggregate, makes a person feel monitored, dependent, or afraid — even without physical violence. It looks the same across every gender.",
    sections: [
      { h: "Common signs", list: [
        "Excessive jealousy — about your friends, your past, your phone.",
        "Monitoring your communications, location, or social media.",
        "Slowly isolating you from friends and family.",
        "Name-calling or emotional humiliation, especially in private.",
        "Financial control — \"our money\" that's really hers/his.",
        "Crossing boundaries you've clearly named, then minimising it.",
        "Threats — sometimes wrapped as jokes.",
        "Manipulation that makes you doubt your own memory.",
        "Walking on eggshells. Always tracking her or his mood.",
      ]},
      { h: "Examples that show up specifically in LGBTQ+ relationships", list: [
        "\"You can't leave me, no one else will get you.\"",
        "Demanding access to chosen-family group chats.",
        "Using shared queer community as a reason to silence concerns: \"don't make us look bad.\"",
        "Withholding affection unless you perform a certain expression of gender or sexuality.",
      ]},
      { h: "If you recognise yourself here",
        body: "Recognition is not a verdict. It's a doorway. You can stay in it, or step through it, in your own time. Sister Signal has more tools when you're ready." },
    ],
  },
  {
    slug: "identity-threats",
    title: "When someone threatens your identity",
    lede: "When a partner uses your sexuality, gender identity, or chosen family as leverage, that is abuse. Identity-based threats are control wearing the costume of intimacy.",
    sections: [
      { h: "What it can look like", list: [
        "Threats to out you to family, work, your church, or your community.",
        "Threats to call your parents and \"tell them what you really are.\"",
        "Holding the keys to community spaces, friend groups, or chosen family that took you years to find.",
        "Using your reputation against you — \"if you leave I'll tell everyone what you did.\"",
        "Pressuring you to stay silent because \"we have to look strong for the community.\"",
        "Telling you that leaving would prove the stereotypes critics use against people like you.",
      ]},
      { h: "What is true",
        body: "Your identity is yours. No relationship is allowed to hold it as collateral. If you've ever stayed because the alternative felt like losing not just a partner but a community, please know: that fear is real, and so is the help. Many advocates and shelters are explicitly affirming. They will not out you, and they will not assume your story." },
      { h: "Gentle next step",
        body: "If outing is the threat, build a small circle of people who already know — even one — who can be your soft landing. You do not have to do this alone." },
    ],
  },
  {
    slug: "both-women",
    title: "\"But we're both women\"",
    lede: "Compassion does not require self-abandonment. Loving someone deeply does not make their cruelty your responsibility.",
    sections: [
      { h: "The myths",
        list: [
          "Myth: \"Abuse between women is just mutual conflict.\" — Conflict is two people disagreeing. Abuse is one person consistently controlling, frightening, or diminishing the other.",
          "Myth: \"Women don't really hurt women.\" — Statistics show abuse in women's same-sex relationships occurs at rates similar to opposite-sex relationships. Silence about it is the problem.",
          "Myth: \"She's been through so much, of course she has a temper.\" — Trauma history can explain behaviour. It does not excuse it. Two things can be true: she is hurting, and she is hurting you.",
        ],
      },
      { h: "Why it's so disorienting",
        body: "Many women describe a particular kind of confusion when their partner is also a woman. The narrative says \"she'll understand me in ways a man couldn't.\" When that same person becomes the source of the harm, the cognitive dissonance is enormous. That confusion is not a sign that you're wrong. It is the sound of a real story not matching the script you were given." },
      { h: "Self-blame",
        body: "Notice if you've started saying \"maybe I'm the problem,\" \"I should be more understanding,\" or \"she didn't mean it.\" Notice how often. These are common landing places in same-sex abuse — the absence of obvious physical difference makes self-blame feel more available." },
      { h: "What is true",
        body: "You can love someone and still need to leave. You can grieve what could have been while protecting what is. You are not betraying queer community by naming what is happening — you are protecting it." },
    ],
  },
  {
    slug: "both-men",
    title: "\"But we're both men\"",
    lede: "Everyone deserves safety and respect. Shame is one of the most effective things abuse uses to keep its grip — and one of the easiest to release once it's named.",
    sections: [
      { h: "Common silences", list: [
        "Fear of not being believed — by friends, by police, by hospitals.",
        "Pressure not to \"betray\" the community by naming abuse inside it.",
        "Internalised messages that \"men handle their own problems.\"",
        "The myth that abuse requires a size difference. It doesn't.",
      ]},
      { h: "What abuse can look like between men", list: [
        "Physical intimidation — looming, blocking exits, breaking objects, driving recklessly.",
        "Emotional abuse — humiliation, especially in front of friends.",
        "Sexual coercion — \"if you really loved me…\"",
        "Threats to out you at work, to immigration, to family.",
        "Using masculinity stereotypes to dismiss your distress: \"don't be soft.\"",
      ]},
      { h: "What is true",
        body: "Being a man does not mean you can't be a victim. The expectation that you should always be able to handle it is itself one of the tactics abuse uses — it is what keeps men quiet. Your story is real. Your safety matters. The Sister Signal Decode tool, library, and Trusted Circle work for you too." },
    ],
  },
  {
    slug: "green-flags",
    title: "Healthy relationship green flags",
    lede: "Most abuse education focuses on what to avoid. Healthy is something different — and worth being able to recognise too. Here is what it can feel like.",
    sections: [
      { h: "What healthy actually looks like", list: [
        "Respect, even in disagreement. The volume of an argument does not include contempt.",
        "Boundaries are received, not negotiated. You don't have to justify a 'no.'",
        "Repair after conflict. Apologies that include what they did, not just how you felt.",
        "Emotional accountability — naming their own part without needing yours first.",
        "Trust that grows. You're not being audited every time you come home.",
        "Independence held, not punished. Your friendships, your hobbies, your alone time stay yours.",
        "Mutual support. They show up for your wins, not just your crises.",
        "Your nervous system is settling, not bracing.",
      ]},
      { h: "If this feels foreign",
        body: "It is normal for healthy to feel boring at first if your body has been calibrated to chaos. The flatness is not absence of love. It is the beginning of safety." },
      { h: "A small practice",
        body: "When you notice yourself in a healthy moment — even small — name it inside your head. \"This is what safe feels like.\" The naming is what rewires." },
    ],
  },
];

const es = [
  {
    slug: "no-gender",
    title: "El abuso no tiene género",
    lede: "El abuso no se define por el género. Es un patrón de control, miedo, manipulación, coerción, intimidación, aislamiento o daño.",
    sections: [
      { h: "Qué significa esto",
        body: "Muchas sobrevivientes en relaciones del mismo sexo y queer luchan por reconocer el abuso porque la sociedad asume en silencio que el abuso solo ocurre entre hombres y mujeres. El patrón es el mismo. El daño es el mismo. Solo el reconocimiento tarda más." },
      { h: "Cómo se ve el patrón, sin importar el género", list: [
        "Una relación donde te sientes con miedo, más pequeña o constantemente equivocada.",
        "Afecto que va y viene de formas que no coinciden con lo que hiciste.",
        "Disculpas que llegan solo cuando estás a punto de irte.",
        "Una necesidad de defenderla o defenderlo frente a tus amistades, incluso cuando estás sufriendo.",
      ]},
      { h: "Lo que es verdadero",
        body: "Si tu sistema nervioso se tensa cada vez que se abre la puerta, eso es información. Si lo estás minimizando porque «las mujeres no lastiman a las mujeres» o «los hombres no lastiman a los hombres» — ese guion viene de fuera de tu relación. No tiene que ser tu guion." },
    ],
  },
  {
    slug: "coercive-control",
    title: "Señales de control coercitivo",
    lede: "El control coercitivo es un patrón de conducta que, en conjunto, hace que una persona se sienta vigilada, dependiente o con miedo — incluso sin violencia física. Se ve igual en todos los géneros.",
    sections: [
      { h: "Señales comunes", list: [
        "Celos excesivos — sobre tus amistades, tu pasado, tu teléfono.",
        "Monitorear tus comunicaciones, ubicación o redes sociales.",
        "Aislarte lentamente de amistades y familia.",
        "Apodos hirientes o humillación emocional, especialmente en privado.",
        "Control financiero — «nuestro dinero» que en realidad es de ella o él.",
        "Cruzar límites que has nombrado claramente, y luego minimizarlo.",
        "Amenazas — a veces envueltas como bromas.",
        "Manipulación que te hace dudar de tu propia memoria.",
        "Caminar sobre cáscaras de huevo. Siempre rastrear su estado de ánimo.",
      ]},
      { h: "Ejemplos que aparecen específicamente en relaciones LGBTQ+", list: [
        "«No puedes dejarme, nadie más te va a entender.»",
        "Exigir acceso a los chats con tu familia elegida.",
        "Usar la comunidad queer compartida para silenciar tus preocupaciones: «no nos hagas quedar mal».",
        "Retener el afecto a menos que expreses cierta versión de tu género o sexualidad.",
      ]},
      { h: "Si te reconoces aquí",
        body: "El reconocimiento no es un veredicto. Es una puerta. Puedes quedarte en ella o cruzarla, a tu propio tiempo. Sister Signal tiene más herramientas cuando estés lista." },
    ],
  },
  {
    slug: "identity-threats",
    title: "Cuando alguien amenaza tu identidad",
    lede: "Cuando una pareja usa tu sexualidad, identidad de género o familia elegida como palanca, eso es abuso. Las amenazas basadas en identidad son control disfrazado de intimidad.",
    sections: [
      { h: "Cómo puede verse", list: [
        "Amenazas de revelar tu orientación a tu familia, trabajo, iglesia o comunidad.",
        "Amenazas de llamar a tus padres y «contarles lo que realmente eres».",
        "Tener las llaves de espacios comunitarios, grupos de amistades o familia elegida que te tomó años encontrar.",
        "Usar tu reputación en tu contra — «si me dejas, le diré a todos lo que hiciste».",
        "Presión para mantenerte en silencio porque «tenemos que vernos fuertes para la comunidad».",
        "Decirte que irte demostraría los estereotipos que la gente usa contra personas como tú.",
      ]},
      { h: "Lo que es verdadero",
        body: "Tu identidad es tuya. Ninguna relación tiene permiso de retenerla como rehén. Si alguna vez te has quedado porque la alternativa se sentía como perder no solo una pareja sino una comunidad — ese miedo es real, y la ayuda también. Muchas defensoras y refugios son explícitamente afirmativos. No te van a sacar del clóset, y no van a asumir tu historia." },
      { h: "Un paso suave",
        body: "Si la amenaza es ser revelada, construye un círculo pequeño de personas que ya lo saben — aunque sea una — que pueda ser tu lugar de aterrizaje suave. No tienes que hacer esto sola." },
    ],
  },
  {
    slug: "both-women",
    title: "«Pero ambas somos mujeres»",
    lede: "La compasión no requiere abandonarte a ti misma. Amar profundamente a alguien no convierte su crueldad en tu responsabilidad.",
    sections: [
      { h: "Los mitos",
        list: [
          "Mito: «El abuso entre mujeres es solo conflicto mutuo.» — El conflicto es dos personas en desacuerdo. El abuso es una persona controlando, asustando o disminuyendo a la otra de forma consistente.",
          "Mito: «Las mujeres en realidad no lastiman a las mujeres.» — Las estadísticas muestran que el abuso en relaciones de mujeres del mismo sexo ocurre a tasas similares a las relaciones heterosexuales. El silencio es el problema.",
          "Mito: «Ha pasado por tanto, claro que tiene mal carácter.» — Una historia de trauma puede explicar la conducta. No la excusa. Dos cosas pueden ser ciertas: ella está sufriendo, y te está lastimando.",
        ],
      },
      { h: "Por qué es tan desorientador",
        body: "Muchas mujeres describen una confusión particular cuando su pareja también es mujer. La narrativa dice «ella va a entenderme de formas que un hombre no podría». Cuando esa misma persona se convierte en la fuente del daño, la disonancia es enorme. Esa confusión no es señal de que estés equivocada. Es el sonido de una historia real que no coincide con el guion que te dieron." },
      { h: "Auto-culpa",
        body: "Nota si has empezado a decir «tal vez yo soy el problema», «debería ser más comprensiva» o «no quiso hacerlo». Nota qué tan seguido. Son lugares comunes donde aterriza el abuso entre mujeres — la ausencia de una diferencia física obvia hace que la auto-culpa se sienta más disponible." },
      { h: "Lo que es verdadero",
        body: "Puedes amar a alguien y aun así necesitar irte. Puedes lamentar lo que pudo haber sido mientras proteges lo que es. No estás traicionando a la comunidad queer al nombrar lo que está pasando — la estás protegiendo." },
    ],
  },
  {
    slug: "both-men",
    title: "«Pero ambos somos hombres»",
    lede: "Todos merecen seguridad y respeto. La vergüenza es una de las cosas más efectivas que usa el abuso para mantenerte agarrado — y una de las más fáciles de soltar una vez nombrada.",
    sections: [
      { h: "Silencios comunes", list: [
        "Miedo de no ser creído — por amistades, por la policía, por hospitales.",
        "Presión de no «traicionar» a la comunidad al nombrar el abuso dentro de ella.",
        "Mensajes internalizados de que «los hombres manejan sus propios problemas».",
        "El mito de que el abuso requiere una diferencia de tamaño. No la requiere.",
      ]},
      { h: "Cómo puede verse el abuso entre hombres", list: [
        "Intimidación física — acercarse amenazante, bloquear salidas, romper objetos, conducir de forma temeraria.",
        "Abuso emocional — humillación, especialmente frente a amistades.",
        "Coerción sexual — «si realmente me amaras…»",
        "Amenazas de revelar tu orientación en el trabajo, a inmigración, a la familia.",
        "Usar estereotipos de masculinidad para descartar tu angustia: «no seas blando».",
      ]},
      { h: "Lo que es verdadero",
        body: "Ser hombre no significa que no puedas ser víctima. La expectativa de que siempre deberías poder manejarlo es en sí misma una de las tácticas que usa el abuso — es lo que mantiene a los hombres en silencio. Tu historia es real. Tu seguridad importa. El Decodificador, la biblioteca y el Círculo de Confianza de Sister Signal también funcionan para ti." },
    ],
  },
  {
    slug: "green-flags",
    title: "Señales verdes de una relación sana",
    lede: "La mayor parte de la educación sobre abuso se enfoca en qué evitar. Lo sano es otra cosa — y vale la pena poder reconocerlo también. Esto es cómo puede sentirse.",
    sections: [
      { h: "Cómo se ve realmente lo sano", list: [
        "Respeto, incluso en desacuerdo. El volumen de una discusión no incluye desprecio.",
        "Los límites se reciben, no se negocian. No tienes que justificar un «no».",
        "Reparación después del conflicto. Disculpas que incluyen lo que hizo, no solo cómo te sentiste.",
        "Responsabilidad emocional — nombra su parte sin necesitar la tuya primero.",
        "Confianza que crece. No te están auditando cada vez que llegas a casa.",
        "Independencia sostenida, no castigada. Tus amistades, tus pasatiempos, tu tiempo a solas siguen siendo tuyos.",
        "Apoyo mutuo. Aparecen para tus victorias, no solo para tus crisis.",
        "Tu sistema nervioso se está calmando, no tensándose.",
      ]},
      { h: "Si esto se siente ajeno",
        body: "Es normal que lo sano se sienta aburrido al principio si tu cuerpo ha sido calibrado al caos. La calma no es ausencia de amor. Es el comienzo de la seguridad." },
      { h: "Una práctica pequeña",
        body: "Cuando te notes en un momento sano — aunque sea pequeño — nómbralo dentro de tu cabeza. «Esto es lo que se siente seguro». Nombrarlo es lo que recablea." },
    ],
  },
];

const REGISTRY = { en, es };

export function getLgbtqContent(lang) {
  return REGISTRY[lang] || REGISTRY.en;
}

export function getLgbtqArticle(slug, lang) {
  return (REGISTRY[lang] || REGISTRY.en).find((a) => a.slug === slug) || null;
}

/**
 * Returns the slug ordering to display, prioritised for the user's
 * relationship context. The full set is always returned — context only
 * changes which one shows first.
 */
export function orderForPartner(partnerGender) {
  const all = ["no-gender", "coercive-control", "identity-threats", "both-women", "both-men", "green-flags"];
  if (partnerGender === "woman") {
    return ["both-women", "no-gender", "coercive-control", "identity-threats", "green-flags", "both-men"];
  }
  if (partnerGender === "man") {
    return ["both-men", "no-gender", "coercive-control", "identity-threats", "green-flags", "both-women"];
  }
  if (partnerGender === "nonbinary") {
    return ["no-gender", "identity-threats", "coercive-control", "green-flags", "both-women", "both-men"];
  }
  return all;
}
